import {
	observable,
	computed,
	action,
	runInAction,
	reaction,
	IReactionDisposer,
} from "mobx";
import DepositFilterStore from "./DepositFilterStore";

import { LoaderStore } from "@state/stores/common";
import { betPeriods } from "@utils/common/BetPeriods";

import { DepositService } from "@services/payment/deposit/DepositService";
import {
	DepositResponseDto,
	PaymentOption,
	UsedPaymentMethodDto,
	DepositBonusInfo,
	PaymentProcessingError,
} from "@api-types/deposit";
import {
	DepositProcess,
	PaymentRequestFilter,
	RawFilterData,
	DepositFinalizeMultistep,
	DepositInitRequestData,
} from "@data-types/deposit";
import { localizationService } from "@state";
import { BaseLookupDto } from "@api-types/lookups/BaseLookupDto";
import { AccountTypes, getUserId, isGamesTheme } from "@utils";
import { UserAccountType } from "@common/constants";

import {
	getRedirectOriginUrl,
	isCapacitorPlatform,
} from "@utils/specific/capacitor/helpers";
import type { RootStore } from "@state/stores/types";
import type RootStoreMobile from "@state/stores/RootStore.mobile";
import { analyticsTracker } from "@utils/analytics";
import { getDefaultOnlineWallet } from "@utils/common/bettingAccountUtils";
import DepositLimitStore from "./DepositLimitStore";
import {
	isBankIdPasswordlessUser,
	startWebStepUp,
	consumeWebStepUp,
} from "@utils/specific/bankid/bankIdStepUp";
import {
	isBankIdInstanceMismatchError,
	BANKID_INSTANCE_MISMATCH_MESSAGE_KEY,
} from "@utils/specific/bankid/bankIdStepUpError";
import { BankIdStepUpAction } from "@data-types/user/BankIdStepUpAction";

enum DepositErrorCodes {
	UserAccountDeactivationGracePeriodExpired = 331,
}

export default class DepositStore {
	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;

		// Helper stores
		this.depositLimitStore = new DepositLimitStore(rootStore, this);
		this.depositFilterStore = new DepositFilterStore(rootStore, this);
		this.loader = new LoaderStore();

		// Method bindings
		this.getDateRange = this.getDateRange.bind(this);
	}

	//#region observables

	@observable partialDepositAmount: number | null = null;

	@observable isStoreInitialized: boolean = false;
	@observable isFetchingData: boolean = true; // When store first starts it will always start fetching
	@observable isRedirectingToPaymentPage: boolean = false;

	@observable depositList: DepositResponseDto | null = null;
	@observable paymentMethods: PaymentOption[];
	@observable userPaymentMethods: UsedPaymentMethodDto[];
	@observable transactionStatuses: BaseLookupDto[];
	@observable paymentOption: PaymentOption | null = null;
	@observable isAmountInserted: boolean = false;
	@observable insertedAmount: number | undefined = 0;
	@observable currentFormData:
		(Omit<DepositProcess, "amount"> & { amount: number }) | null = null;
	@observable isMultiStepDeposit: boolean = false;
	@observable isPasswordConfirmation: boolean = false;
	@observable isPasswordConfirmationRequired: boolean = false;
	@observable isBonusConfirmation: boolean = false;
	@observable paymentModeHash: string = "";
	@observable submittedWallet: string | null = null;

	@observable paymentProcessingMode: number;
	@observable withdrawalMethodSelectionMode: number;

	@observable iFrameRedirectUrl: string | null = null;

	@observable notVerifiedMessage: string | null = null;

	@observable limitExcededInfo: {
		limitRequestValue: string;
		limitRequestDate: string;
		showCreditRating: boolean;
		showVerificationLink: boolean;
	} | null = null;

	@computed get selectedPaymentMethod() {
		return this.paymentMethods?.find(
			(method) =>
				method.paymentMethodId === this.currentFormData?.paymentMethodId
		);
	}

	@computed get userPaymentSavedMethods() {
		return this.paymentMethods?.filter(
			(method) =>
				method.hasPaymentReference === true ||
				method.hasMultipleReferences === true
		);
	}

	@computed get otherPaymentMethods() {
		return this.paymentMethods?.filter(
			(method) =>
				method.hasPaymentReference === false &&
				method.hasMultipleReferences === false
		);
	}

	@computed get onlineWallet() {
		return getDefaultOnlineWallet(
			this.rootStore.userAuthStore.userAccounts
		);
	}

	@computed get casinoWallet() {
		return this.rootStore.userAuthStore.userAccounts?.find(
			(wallet) => wallet.abrv === UserAccountType.CasinoAccount
		);
	}

	@computed get liveCasinoWallet() {
		return this.rootStore.userAuthStore.userAccounts?.find(
			(wallet) => wallet.abrv === UserAccountType.LiveCasinoAccount
		);
	}

	@computed get gamesWallet() {
		return this.rootStore.userAuthStore.userAccounts?.find(
			(wallet) => wallet.abrv === UserAccountType.Games
		);
	}

	@computed get betPeriods() {
		return betPeriods;
	}

	@computed get shouldShowWalletSelectors(): boolean {
		if (this.rootStore.userAuthStore.userAccounts == null) {
			return false;
		}
		return this.rootStore.userAuthStore.userAccounts.length > 1;
	}

	@computed get shouldShowEmptyState(): boolean {
		return (
			this.isStoreInitialized &&
			!this.isFetchingData &&
			this.paymentMethods.length == 0
		);
	}

	pageSize: number = 10;

	loader: LoaderStore;
	depositFilterStore: DepositFilterStore;
	depositLimitStore: DepositLimitStore;
	filterReaction: IReactionDisposer | null;
	rootStore: RootStore;

	@observable totalItems: number = 0;
	@observable initialLoad: boolean = true;

	@observable depositBonusInfo: DepositBonusInfo | null = null;

	@observable paymentMethodError: {
		message: string;
		errorCode: number;
	} | null = null;

	//#endregion observables

	//#region computed

	@computed get isLoading(): boolean {
		return this.loader.isLoading;
	}

	@computed get isLoadingProcess(): boolean {
		return this.loader.isLoadingProcess;
	}

	@computed get getPartialDepositAmount(): number | null {
		if (this.partialDepositAmount) {
			return this.partialDepositAmount;
		} else {
			return null;
		}
	}

	//#endregion computed

	//#region actions

	@action.bound
	setPartialDepositAmount(partialDepositAmount: number | null) {
		this.partialDepositAmount = partialDepositAmount;
	}

	@action.bound
	async onInitialize(): Promise<void> {
		this.loader.suspend();

		try {
			const paymentMethods = await DepositService.getAvailableMethods();

			runInAction(() => {
				this.paymentMethods = paymentMethods.availablePaymentMethods;
				this.paymentModeHash = paymentMethods.paymentModeHash;
				this.paymentProcessingMode =
					paymentMethods.paymentProcessingMode;
				this.withdrawalMethodSelectionMode =
					paymentMethods.withdrawalMethodSelectionMode;
				this.isPasswordConfirmation =
					paymentMethods.isPasswordConfirmationRequired;
				this.isPasswordConfirmationRequired =
					paymentMethods.isPasswordConfirmationRequired;
				this.rootStore.userAuthStore.userDataConfirmationStore.setShouldShowDataConfirmationEmptyState(
					false
				);
			});
		} catch (error) {
			console.error(error);

			this.loader.resume();

			this.paymentMethodError = {
				message:
					error?.data?.errorMessage?.shift() ||
					localizationService.t("ERROR.TITLE"),
				errorCode: error?.data?.error,
			};

			if (
				error?.data?.error ==
				PaymentProcessingError.GracePeriodConfirmationError
			) {
				this.rootStore.gracePeriodStore.showPopup();
				this.rootStore.retryHandlerStore.setRetry(() =>
					this.onInitialize()
				);
				this.rootStore.gracePeriodStore.setShouldShowGracePeriodEmptyState(
					true
				);
				this.paymentMethodError = null;
			}

			if (
				error?.data?.error ==
				PaymentProcessingError.UserDataConfirmationError
			) {
				this.rootStore.userAuthStore.userDataConfirmationStore.setShouldShowDataConfirmationEmptyState(
					true
				);
				return;
			}
		}

		runInAction(() => {
			this.isStoreInitialized = true;
			this.loader.resume();
		});

		await this.resumeBankIdStepUp();

		this.depositFilterStore.onInitialize();

		this.disposeFilterReaction();
		this.filterReaction = reaction(
			() => ({
				filter: this.depositFilterStore.filter,
				isStoreInitialized: this.isStoreInitialized,
			}),
			() => {
				if (
					this.isStoreInitialized && // Wait for lookups to load
					!this.depositFilterStore.isDefaultFilter // Don't fetch for default filter
				) {
					this.fetchDeposit(this.depositFilterStore.filter);
				}
			},
			{
				fireImmediately: true,
			}
		);
	}

	@action.bound
	async fetchUserPaymentMethods(): Promise<void> {
		try {
			this.loader.suspend();
			const userPaymentMethods =
				await DepositService.getUserPaymentMethods();
			runInAction(() => {
				this.userPaymentMethods = userPaymentMethods;
			});
		} catch (error) {
			console.error(error);
			const errorMessage =
				error.data?.errorMessage?.shift() ||
				localizationService.t("ERROR.TITLE");
			this.rootStore.notificationStore.error(errorMessage);
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	async fetchTransactionStatuses() {
		try {
			this.loader.suspend();
			const transactionStatuses =
				await DepositService.getTransactionStatuses();
			runInAction(() => {
				this.transactionStatuses = transactionStatuses.item;
			});
		} catch (error) {
			console.error(error);
			const errorMessage =
				error.data?.errorMessage?.shift() ||
				localizationService.t("ERROR.TITLE");
			this.rootStore.notificationStore.error(errorMessage);
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	async fetchDeposit(filter: PaymentRequestFilter): Promise<void> {
		this.loader.suspend();
		this.isFetchingData = true;

		try {
			const requestFilter: PaymentRequestFilter = {
				from: filter.from,
				to: filter.to,
				pageNumber: filter.pageNumber,
				pageSize: this.pageSize,
				userId: getUserId(
					this.rootStore.userAuthStore.getUserAccountType(
						AccountTypes.ONLINE
					)
				),
				isDeposit: true,
				bettingAccountTypeId:
					isGamesTheme() && this.gamesWallet?.accountTypeId
						? this.gamesWallet.accountTypeId
						: filter.bettingAccountTypeId,
			};

			if (filter.paymentMethodId !== "all") {
				requestFilter.paymentMethodId = filter.paymentMethodId;
			}

			if (filter.transactionStatusId !== "all") {
				requestFilter.transactionStatusId = filter.transactionStatusId;
			}

			const response = await DepositService.getDepositData(requestFilter);

			const data = {
				...response,
				item: response.item.map((item) => ({
					...item,
					status: this.transactionStatuses?.find(
						(status) => status.id === item.transactionStatusId
					),
				})),
			};

			runInAction(() => {
				this.depositList = data;
				this.totalItems = data.totalRecords;
			});
		} catch (error) {
			console.error(error);

			const errorMessage =
				error.data?.errorMessage?.shift() ||
				localizationService.t("ERROR.TITLE");

			this.rootStore.notificationStore.error(errorMessage);

			runInAction(() => {
				this.depositList = {
					item: [],
					page: 1,
					totalRecords: 0,
				};
			});
		} finally {
			this.loader.resume();
			this.isFetchingData = false;
			this.initialLoad = false;
		}
	}

	@action.bound
	refresh(): void {
		this.fetchDeposit(this.depositFilterStore.filter);
	}

	@action.bound
	submitFilter(data: RawFilterData): void {
		this.depositFilterStore.setRawFilterData(data);
	}

	@action.bound
	onPageChange(newPageNumber: number): void {
		this.depositFilterStore.setPageNumber(newPageNumber);
	}

	@action.bound
	setPaymentOption(paymentOption: PaymentOption | null): void {
		this.paymentOption = paymentOption;
	}

	@action.bound
	setSubmittedWallet(wallet: string): void {
		this.submittedWallet = wallet;
	}

	@action.bound
	setAmountFlag(state: boolean): void {
		this.isAmountInserted = state;
	}

	@action.bound
	setInsertedAmount(amount?: number): void {
		this.insertedAmount = amount;
	}

	@action.bound
	setLimitExcededInfo(
		value: {
			limitRequestValue: string;
			limitRequestDate: string;
			showCreditRating: boolean;
			showVerificationLink: boolean;
		} | null
	) {
		this.limitExcededInfo = value;
	}

	@action.bound
	async resumeBankIdStepUp(): Promise<void> {
		const stepUp = consumeWebStepUp(BankIdStepUpAction.Deposit);

		if (stepUp.status === "success") {
			await this.finalizeBankIdDeposit(stepUp.payload, stepUp.sessionId);
		} else if (stepUp.status === "failed") {
			this.rootStore.notificationStore.error(
				localizationService.t(stepUp.messageKey)
			);
		}
	}

	@action.bound
	async initializeNewDepositProcess(
		data: DepositProcess,
		bankIdSessionId?: string
	): Promise<void> {
		const request = this.buildDepositRequest(data, bankIdSessionId);
		await this.submitDepositRequest(request, data);
	}

	buildDepositRequest(
		data: DepositProcess,
		bankIdSessionId?: string
	): DepositInitRequestData {
		const paymentMethodObject = this.paymentMethods.find(
			(method) => method.paymentMethodId === data.paymentMethodId
		);

		return {
			amount: parseFloat(data.amount),
			paymentMethodId: data.paymentMethodId,
			bettingAccountTypeId:
				this.submittedWallet || data.bettingAccountTypeId,
			paymentProviderId: paymentMethodObject?.paymentProviderId!,
			isDeposit: true,
			paymentModeHash: this.paymentModeHash,
			paymentProcessingMode: this.paymentProcessingMode,
			withdrawalMethodSelectionMode: this.withdrawalMethodSelectionMode,
			bankIdSessionId,
			isPasswordConfirmationRequired: this.isPasswordConfirmationRequired,
			redirectUrls: {
				successUrl: getRedirectOriginUrl("payment-info/success"),
				backUrl: getRedirectOriginUrl("deposit"),
				failureUrl: getRedirectOriginUrl("payment-info/failure"),
				pendingUrl: getRedirectOriginUrl("payment-info/pending"),
				returnUrl: getRedirectOriginUrl("deposit-info"),
			},
		};
	}

	@action.bound
	async submitDepositRequest(
		request: DepositInitRequestData,
		data?: DepositProcess
	): Promise<void> {
		this.loader.suspend();

		this.currentFormData = data
			? {
					...data,
					amount:
						this.partialDepositAmount || parseFloat(data.amount),
				}
			: {
					paymentMethodId: request.paymentMethodId,
					amount: this.partialDepositAmount || request.amount!,
					bettingAccountTypeId: request.bettingAccountTypeId,
				};

		const paymentMethodObject = this.paymentMethods.find(
			(method) => method.paymentMethodId === request.paymentMethodId
		);

		if (paymentMethodObject?.displayName) {
			analyticsTracker.trackDepositInitiated(
				paymentMethodObject.displayName,
				request.amount!
			);
		}
		try {
			const initResponse =
				await DepositService.initializeNewDepositProcess(request);

			if (
				initResponse.isValid === true &&
				!initResponse.redirectUrl &&
				initResponse.isPasswordConfirmationRequired &&
				isBankIdPasswordlessUser(this.rootStore.userAuthStore.user) &&
				!request.bankIdSessionId
			) {
				request.isPasswordConfirmationRequired =
					initResponse.isPasswordConfirmationRequired;

				if (isCapacitorPlatform()) {
					this.loader.resume();
					(
						this.rootStore as unknown as RootStoreMobile
					).bankIdCapacitorStore.activateStepUp({
						onValidated: (sessionId) =>
							this.finalizeBankIdDeposit(request, sessionId),
					});
				} else {
					try {
						await startWebStepUp(
							BankIdStepUpAction.Deposit,
							request
						);
					} catch (e) {
						this.loader.resume();
						console.error(e);
						this.rootStore.notificationStore.error(
							localizationService.t(
								"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_NOT_VALID"
							)
						);
					}
				}
				return;
			}

			runInAction(() => {
				if (initResponse.isValid === true) {
					//comment this if case if u wish to go to final step
					if (initResponse.redirectUrl) {
						this.isRedirectingToPaymentPage = true;
					} else {
						//set all this to true if u wish to see final step and password and bonus
						this.isMultiStepDeposit =
							initResponse.isPasswordConfirmationRequired ||
							initResponse.isBonusAvailable;
						this.isPasswordConfirmation =
							initResponse.isPasswordConfirmationRequired;

						this.isBonusConfirmation =
							initResponse.isBonusAvailable;
						this.depositBonusInfo = initResponse.depositBonusInfo;
					}
				}
			});
			if (this.isRedirectingToPaymentPage && initResponse.redirectUrl) {
				window.location.href = initResponse.redirectUrl;
				if (isCapacitorPlatform()) {
					//only on native
					//reset state so user can submit again if we comes back from browser into app
					runInAction(() => {
						this.isRedirectingToPaymentPage = false;
					});
					this.loader.resume();
				}
			}
		} catch (error) {
			if (error.userAccountDeactivationGracePeriodExpirationDate) {
				this.rootStore.userAuthStore.setWalletGracePeriod(
					error.userAccountDeactivationGracePeriodExpirationDate
				);
			}
			console.error(error);
			if (error?.data?.error === 324) {
				this.setLimitExcededInfo({
					limitRequestDate: error.data.limitRequestDate,
					limitRequestValue: error.data.limitRequestAmount,
					showCreditRating: error.data?.showCreditRating,
					showVerificationLink: error.data?.showVerificationLink,
				});
			} else if (error?.data?.error === 329) {
				this.depositLimitStore.activateDepositLimitPopup(error?.data);
				return;
			} else if (
				error?.data?.error ===
				DepositErrorCodes.UserAccountDeactivationGracePeriodExpired
			) {
				this.rootStore.walletGracePeriodPopupStore.showPopup(
					error?.data
						?.userAccountDeactivationGracePeriodExpirationDate
				);
				return;
			} else if (error?.data?.error === 69) {
				this.rootStore.accountVerificationStore.showPopup(
					"user-profile-recheck",
					"ACCOUNT_RECHECK.TITLE",
					error?.data?.errorMessage?.shift()
				);
			} else if (error?.data?.error === 15) {
				this.rootStore.accountVerificationStore.showPopup(
					"account-settings/my-limits",
					"COMMON.NAVIGATION.MY_LIMITS",
					error?.data?.errorMessage?.shift()
				);
			} else if (error?.data?.error === 400242) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_VALIDATION_ERROR"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.data?.error === 400245) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_DOWN"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.data?.error === 400250) {
				const errorMessage = localizationService.t(
					"DEPOSIT.LUGAS_DEPOSIT_EXCEEDS_BUDGET"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.data?.error === 400256) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_UNSPECIFIED"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else {
				const errorMessage =
					error.data?.errorMessage?.shift() ||
					localizationService.t("ERROR.TITLE");
				this.rootStore.notificationStore.error(errorMessage);
			}
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	async finalizeMultiStepDeposit(data: {
		isBonusAccepted?: boolean;
		password?: string;
		bettingAccountTypeId?: string;
		isGiG?: boolean;
	}) {
		const paymentMethodObject = this.paymentMethods.find(
			(method) =>
				method.paymentMethodId === this.currentFormData?.paymentMethodId
		);

		const finalizeObject: DepositFinalizeMultistep = {
			amount: this.currentFormData?.amount,
			bettingAccountTypeId:
				this.submittedWallet ||
				this.currentFormData?.bettingAccountTypeId,
			isDeposit: true,
			isPasswordConfirmationRequired: this.isPasswordConfirmation,
			paymentModeHash: this.paymentModeHash,
			paymentMethodId: this.currentFormData?.paymentMethodId,
			userPassword: data?.password,
			paymentProviderId: paymentMethodObject?.paymentProviderId,
			isBonusAccepted: data?.isBonusAccepted,
			paymentProcessingMode: this.paymentProcessingMode,
			withdrawalMethodSelectionMode: this.withdrawalMethodSelectionMode,
			redirectUrls: {
				successUrl: getRedirectOriginUrl("payment-info/success"),
				backUrl: getRedirectOriginUrl("deposit"),
				failureUrl: getRedirectOriginUrl("payment-info/failure"),
				pendingUrl: getRedirectOriginUrl("payment-info/pending"),
				returnUrl: getRedirectOriginUrl("deposit-info"),
			},
		};

		await this.submitFinalizeRequest(finalizeObject);
	}

	@action.bound
	async finalizeBankIdDeposit(
		request: DepositInitRequestData,
		bankIdSessionId: string
	): Promise<void> {
		const finalizeObject: DepositFinalizeMultistep = {
			amount: request.amount,
			bettingAccountTypeId: request.bettingAccountTypeId,
			isDeposit: true,
			isPasswordConfirmationRequired:
				request.isPasswordConfirmationRequired,
			paymentModeHash: request.paymentModeHash,
			paymentMethodId: request.paymentMethodId,
			paymentProviderId: request.paymentProviderId,
			bankIdSessionId,
			paymentProcessingMode: request.paymentProcessingMode,
			withdrawalMethodSelectionMode:
				request.withdrawalMethodSelectionMode,
			redirectUrls: request.redirectUrls,
		};

		await this.submitFinalizeRequest(finalizeObject);
	}

	@action.bound
	async submitFinalizeRequest(
		finalizeObject: DepositFinalizeMultistep
	): Promise<void> {
		this.loader.suspend();

		const paymentMethodObject = this.paymentMethods.find(
			(method) =>
				method.paymentMethodId === finalizeObject.paymentMethodId
		);

		try {
			const finalizeResponse =
				await DepositService.finalizeMultiStepDeposit(finalizeObject);

			runInAction(() => {
				if (finalizeResponse.isValid === true) {
					if (finalizeResponse.redirectUrl) {
						if (paymentMethodObject) {
							analyticsTracker.trackDepositCompleted(
								paymentMethodObject?.displayName,
								Number(finalizeObject.amount)
							);
						}

						this.isRedirectingToPaymentPage = true;
					}
				}
			});

			if (
				this.isRedirectingToPaymentPage &&
				finalizeResponse.redirectUrl
			) {
				window.location.href = finalizeResponse.redirectUrl;

				if (isCapacitorPlatform()) {
					//reset state so user can submit again if we comes back from browser into app
					runInAction(() => {
						this.isRedirectingToPaymentPage = false;
					});
					this.loader.resume();
				}
			}
		} catch (error) {
			console.error(error);
			if (isBankIdInstanceMismatchError(error)) {
				this.rootStore.notificationStore.error(
					localizationService.t(BANKID_INSTANCE_MISMATCH_MESSAGE_KEY)
				);
				return;
			}

			if (error?.data?.error === 324) {
				this.setLimitExcededInfo({
					limitRequestDate: error.data.limitRequestDate,
					limitRequestValue: error.data.limitRequestAmount,
					showCreditRating: error.data?.showCreditRating,
					showVerificationLink: error.data?.showVerificationLink,
				});
				return;
			}

			if (error?.data?.error === 329) {
				this.depositLimitStore.activateDepositLimitPopup(error?.data);
				return;
			}

			if (error?.data?.error === 69) {
				this.rootStore.accountVerificationStore.showPopup(
					"user-profile-recheck",
					"ACCOUNT_RECHECK.TITLE",
					error?.data?.errorMessage?.shift()
				);
				return;
			}

			if (error?.data?.errorCode === 400176) {
				this.rootStore.notificationStore.error(
					localizationService.t(
						"error-handling:INVALID_PASSWORD"
					)
				);
				return;
			}
			const errorMessage =
				error.data?.errorMessage?.shift() ||
				error.data?.details ||
				localizationService.t("ERROR.TITLE");
			this.rootStore.notificationStore.error(errorMessage);
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	resetMultiStepFlags(): void {
		this.isPasswordConfirmation = false;
		this.isBonusConfirmation = false;
		this.isMultiStepDeposit = false;
	}

	//#endregion actions

	//#region helpers

	getDateRange(data: RawFilterData): {
		from: string | null | undefined;
		to: string | null | undefined;
	} | null {
		const { period, from, to } = data;

		if (
			(period == null && from == null && to == null) ||
			period === "all"
		) {
			return null;
		}

		if (period !== "custom") {
			const timeInterval = this.betPeriods
				.find((p) => p.value === period)
				?.getTimeInterval();
			if (timeInterval == null) {
				// Return default
				return this.betPeriods[0].getTimeInterval();
			}
			return timeInterval;
		}

		return { from, to };
	}

	//#endregion helpers

	//#region disposers

	@action.bound
	onDispose(): void {
		this.depositFilterStore.onDispose();
		this.disposeFilterReaction();

		this.isStoreInitialized = false;
		this.isFetchingData = true;
		this.paymentMethodError = null;
		this.limitExcededInfo = null;

		this.disposeCurrentDepositState();
	}

	@action.bound
	disposeCurrentDepositState(): void {
		this.depositList = null;
		this.initialLoad = true;
		this.pageSize = 10;
		this.totalItems = 0;
		this.limitExcededInfo = null;
	}

	@action.bound
	disposeFilterReaction(): void {
		if (this.filterReaction != null) {
			this.filterReaction();
			this.filterReaction = null;
		}
	}

	@action.bound
	disposeNewDepositProcess(): void {
		this.isAmountInserted = false;
		this.insertedAmount = 0;
		this.limitExcededInfo = null;
	}

	//#endregion disposers
}
